// @flow
import React, { Component } from 'react';
import type { Node } from 'react';
import PropTypes from 'prop-types';

// Helpers
import classNames from 'classnames';
import { gtmEventPush } from 'utils/gtm/events';
import { formatExternalLink } from 'utils/utmLinks';

import Icon from 'Common/Icon';

import styles from 'components/CardVertical.scss';
import Picture from 'Common/Picture';
import Image from 'Common/Image';

import Button from 'Common/Button';
import isWindow from 'Common/isWindow';

/**
 * <Card /> component.
 * @return {class} returns card instance
 */
class CardVertical extends Component<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      imageSrc: this.props.image,
      showDealerImage: true
    };
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {string} id - card id.
   * @prop {string} name - card name.
   * @prop {number} price - card price.
   * @prop {string} year - card year.
   * @prop {oneOfType} distanceOrTown - card distanceOrTown.
   * @prop {number} mileage - card mileage.
   * @prop {string} fuelType - card fuelType.
   * @prop {number} doors - card doors.
   * @prop {string} transmission - card transmission.
   * @prop {string} image - card image.
   * @prop {object} history - history.
   * @prop {object} item - item.
   * @prop {func} handleClick: handleClick
   */
  static propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    make: PropTypes.string,
    range: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    year: PropTypes.string,
    distanceOrTown: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mileage: PropTypes.number,
    fuelType: PropTypes.string,
    doors: PropTypes.number,
    transmission: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.object,
    dealerImage: PropTypes.string,
    cardLayout: PropTypes.string,
    history: PropTypes.object,
    item: PropTypes.object,
    handleClick: PropTypes.func,
    network: PropTypes.string,
    gcLid: PropTypes.string,
    gtmSectionName: PropTypes.string.isRequired
  };

  /**
   * Implements defaultProps().
   */
  static defaultProps: Object = {
    cardLayout: 'horizontal'
  };

  setFallbackDealerLogo: () => void = () => {
    this.setState({
      showDealerImage: false
    });
  };

  updatedRoute: (path: string) => void = path => {
    this.props.history.push(path);
  };

  handleClick: (e: SyntheticEvent<HTMLButtonElement>) => void = e => {
    const { item, handleClick } = this.props;
    e.preventDefault();
    handleClick(item);
    const url = this.generateUrl();
    isWindow && window.open(url, '_blank');
  };

  handleParentClick: (
    e: SyntheticEvent<HTMLButtonElement>,
    name: string
  ) => void = (e, name) => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: `Popular cars - ${name}`
      }
    });
    this.handleClick(e);
  };

  handleClickChild: (
    e: SyntheticEvent<HTMLButtonElement>,
    name: string
  ) => void = (e, name) => {
    // Stop the parent onClick from happening too.
    e.stopPropagation();

    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: `Popular cars - ${name}`
      }
    });
    this.handleClick(e);
  };

  generateUrl: () => string = () => {
    const { network, gcLid, url } = this.props;

    const linkParams = {
      link: url,
      network,
      gcLid
    };
    const uri = formatExternalLink(linkParams);
    return uri;
  };

  /**
   * Renders <Card /> component.
   *
   * @return {JSX} - renders a logo.
   */
  render(): Node {
    const {
      name,
      price,
      year,
      distanceOrTown,
      mileage,
      fuelType,
      doors,
      transmission,
      dealerImage,
      cardLayout,
      image
    } = this.props;

    const url = this.generateUrl();
    const { card, classCardVertical } = styles;

    return (
      <a
        href={url}
        onClick={e => this.handleParentClick(e, name)}
        rel="nofollow"
        className={classNames([
          cardLayout === 'vertical' ? classCardVertical : card
        ])}
      >
        {image && (
          <div>
            <Picture
              pictures={{
                ...image,
                title: name
              }}
            />
          </div>
        )}
        {!image && (
          <div>
            <Image
              src={`${process.env.PUBLIC_URL || ''}/images/fallback.png`}
              alt={'Image unavailable'}
              title={'Image unavailable'}
            />
          </div>
        )}
        <div>
          <h3 className={styles.name}>{name}</h3>
          <div className={styles.horizontalDetails}>
            <div className={styles.detailsWrapper}>
              <div className={styles.price}>{price}</div>
              <Button
                extendStyle={styles.button}
                rel="nofollow"
                buttonText={'View details'}
                onClick={e => this.handleClickChild(e, name)}
              />
            </div>
            <div className={styles.detailsWrapper}>
              <div className={styles.year}>
                <Icon
                  className={classNames(styles.icon)}
                  iconName={'calendar'}
                  size={'medium'}
                />
                {year}
              </div>
              {!!distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTown
                  )}
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {distanceOrTown}
                </div>
              )}
            </div>
            <div>
              <div className={styles.detailWrapper}>
                <div className={styles.detail} data-mileage>
                  <Icon
                    className={styles.icon}
                    iconName={'road'}
                    size={'medium'}
                  />
                  {mileage && mileage.toLocaleString('en')} Miles
                </div>

                {fuelType && (
                  <div className={styles.detail} data-fuel-type>
                    <Icon
                      className={styles.icon}
                      iconName={'fuel-tank'}
                      size={'medium'}
                    />
                    {fuelType}
                  </div>
                )}
                {transmission && (
                  <div className={styles.detail} data-transmission>
                    <Icon
                      className={styles.icon}
                      iconName={'gearbox'}
                      size={'medium'}
                    />
                    {transmission !== 'N/A'
                      ? transmission.toLowerCase()
                      : transmission}
                  </div>
                )}
                {doors && (
                  <div className={styles.detail} data-doors>
                    <Icon
                      className={styles.icon}
                      iconName={'door'}
                      size={'medium'}
                    />
                    {doors} Doors
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.details}>
            <div>
              <div className={styles.year}>
                <Icon
                  className={classNames(styles.icon)}
                  iconName={'calendar'}
                  size={'medium'}
                />
                {year}
              </div>
              {price && <div className={styles.price}>{price}</div>}

              {!!distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTownMobile
                  )}
                  data-distance-or-town
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {typeof distanceOrTown === 'string'
                    ? distanceOrTown
                    : distanceOrTown}
                </div>
              )}
              <div className={styles.detailWrapper}>
                <div className={styles.detail} data-mileage>
                  <Icon
                    className={styles.icon}
                    iconName={'road'}
                    size={'medium'}
                  />
                  {mileage && mileage.toLocaleString('en')} Miles
                </div>

                {fuelType && (
                  <div className={styles.detail} data-fuel-type>
                    <Icon
                      className={styles.icon}
                      iconName={'fuel-tank'}
                      size={'medium'}
                    />
                    {fuelType}
                  </div>
                )}
                {transmission && (
                  <div className={styles.detail} data-transmission>
                    <Icon
                      className={styles.icon}
                      iconName={'gearbox'}
                      size={'medium'}
                    />
                    {transmission !== 'N/A'
                      ? transmission.toLowerCase()
                      : transmission}
                  </div>
                )}
                {doors && (
                  <div className={styles.detail} data-doors>
                    <Icon
                      className={styles.icon}
                      iconName={'door'}
                      size={'medium'}
                    />
                    {doors} Doors
                  </div>
                )}
              </div>
            </div>
            <div>
              <Icon iconName="open-window" />
              <Button
                extendStyle={styles.button}
                onClick={e => this.handleClickChild(e, name)}
                rel="nofollow"
                buttonText={'View details'}
                buttonSuffix={
                  <Icon iconName="open-window" extendStyle={styles.icon} />
                }
              />
              {!!distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTown
                  )}
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {distanceOrTown}
                </div>
              )}
              {this.state.showDealerImage && dealerImage && (
                <div className={styles.logoWrapper}>
                  <img
                    className={styles.logo}
                    src={dealerImage}
                    alt="Dealer logo"
                    onError={this.setFallbackDealerLogo}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </a>
    );
  }
}

export default CardVertical;



// WEBPACK FOOTER //
// ./src/Common/CardVertical/index.js